/**
 * Platform module inventory — the console reflection of `solum doctor` + the
 * applied license entitlements.
 *
 * TWO HALVES, and keeping them apart is the whole point of this file.
 *
 *   · the FACTS — version, migration count, dependencies, kind, audience,
 *     sellable, schema — come from `virtual:solum-module-facts`, which
 *     scripts/gen-console-modules.mjs computes off each module's `module.json`
 *     at build time (MB-INFRA#239). They are never typed or committed here.
 *   · the EDITORIAL — label, blurb, group and edition — is written by a
 *     person and lives in `moduleEditorial.json`, keyed by module name.
 *
 * The editorial half moved OUT of this file (into JSON) when it acquired a
 * second reader: `solum publish-catalog` writes the same label, category,
 * summary and edition into `controlplane.marketplace_module`, and a vendor
 * catalogue that disagrees with the customer's own Module Manager about what a
 * module is called is two sources for one truth. The fallback derivations moved
 * with it, as data, for the same reason.
 *
 * This file used to carry both, with a header claiming the numbers "mirror each
 * module's module.json". They had not for a long time: `afd` was listed as
 * 0.12.0 with 12 migrations against an actual 0.23.0 with 24; `time` 0.1.0/1
 * against 0.4.0/5; `engagements` 0.1.0/1 against 0.8.0/8; `portal` 0.2.0/2
 * against 0.32.0/32 — and sixteen modules in the tree had no row at all, so the
 * Module Manager simply did not know they existed. `applied` was typed equal to
 * `migrations` for every row, which is also the drift indicator, so the surface
 * an operator uses to decide whether a box needs `solum migrate` could only ever
 * report "up to date" (#561 defect 4).
 *
 * `applied` is now `number | null`, and the static inventory answers `null`: a
 * file in a repository cannot know what a particular deployment has applied. In
 * a LIVE deployment the Module Manager reads `core.installed_modules()` +
 * `core.entitlement()`; on the fixture transport the card renders `—/N` rather
 * than asserting an absence of drift it cannot see.
 *
 * The governed truth this mirrors is unchanged:
 *  - install/uninstall is the `solum` CLI (entitlement-gated, topological,
 *    transactional) — never a click, because click-to-install is a supply-chain
 *    hole a sovereign box refuses.
 *  - entitlements are MINTED OFF-BOX by Moonbase's control plane (Ed25519-signed)
 *    and verified offline — a customer never self-grants a module.
 *  - expiry → read-only + banner, never data loss; the deployment-wide seat cap
 *    refuses only a role assignment that would raise the active human count.
 */
import { MODULE_FACTS, type ModuleFacts, type ModuleState } from 'virtual:solum-module-facts'
import EDITORIAL_FILE from './moduleEditorial.json'

export type { ModuleState }
export type Edition = 'Platform' | 'Included' | 'Bridge' | 'Free' | 'Pro' | 'Premium' | 'Internal'

export interface ModuleRow extends ModuleFacts {
  label: string
  blurb: string
  group: string
  /** Applied on this box. `null` = not known from a static inventory; a live
   *  deployment fills it in. Drift is `applied !== null && applied < migrations`. */
  applied: number | null
  edition: Edition
  entitled: boolean
}

// The fabricated `FLEET` licence lived here — deployment id, "Moonbase BV",
// "valid until 2027-06-01", status "active" — and rendered on a LIVE box, not
// only in the sandbox. Deleted 2026-08-06 (Christopher): nothing mints a licence
// yet (`issue_license` has no non-test caller, there is no `solum license issue`
// and no console UI), so every field of it was a claim about a security control
// that no signature backed. The surfaces show nothing until a real licence is
// read.
interface Editorial {
  label: string
  blurb: string
  group: string
  edition?: Edition
}

/**
 * The hand-written half, read from `moduleEditorial.json`. A module with no
 * entry still RENDERS — with a derived label and a derived edition — because
 * the alternative is what this file used to do: leave sixteen shipped modules
 * invisible until somebody remembered to type a row. A missing blurb is a gap
 * in the copy; a missing row was a lie about the inventory.
 */
const EDITORIAL = EDITORIAL_FILE.editorial as Record<string, Editorial>
const FALLBACK = EDITORIAL_FILE.fallback

/** A readable label for a module nobody has written copy for yet. */
function derivedLabel(key: string): string {
  return key.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ↔ ')
}

/**
 * The edition a module gets when the editorial does not name one — DERIVED from
 * the manifest rather than guessed: a bridge is a Bridge, a Moonbase-internal
 * module is Internal, an unsellable one is Included, and the rest are Pro.
 *
 * The four answers are DATA (`fallback.edition` in moduleEditorial.json), not
 * literals here, because the marketplace publisher derives the same thing for
 * the same modules and two copies of a commercial rule is how a module acquires
 * two editions. Only the evaluation ORDER lives in code, and it is stated in
 * the JSON's own comment beside the table.
 */
function derivedEdition(f: ModuleFacts): Edition {
  if (f.key === 'core') return FALLBACK.edition.core as Edition
  if (f.audience === 'moonbase-internal') return FALLBACK.edition.moonbaseInternal as Edition
  if (f.kind === 'bridge') return FALLBACK.edition.bridge as Edition
  return (f.sellable ? FALLBACK.edition.sellable : FALLBACK.edition.unsellable) as Edition
}

export const MODULES: ModuleRow[] = MODULE_FACTS.map((f) => {
  const e = EDITORIAL[f.key]
  return {
    ...f,
    label: e?.label ?? derivedLabel(f.key),
    blurb: e?.blurb ?? (f.kind === 'bridge'
      ? `Optional bridge — connects ${f.deps.filter((d) => d !== 'core').join(' and ')}. Both sides run standalone without it.`
      : 'No description written yet.'),
    group: e?.group ?? (f.kind === 'bridge' ? FALLBACK.group.bridge
      : f.state === 'internal' ? FALLBACK.group.moonbaseInternal : FALLBACK.group.default),
    // Unknown from a static inventory — see the header. Never `migrations`.
    applied: null,
    edition: e?.edition ?? derivedEdition(f),
    // Entitlement is a property of the LICENSE on the box, not of the repo. The
    // fixture entitles everything installable; the internal modules stay behind
    // the profile gate in `registry.ts`.
    entitled: f.state !== 'planned',
  }
})
